import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, AlertTriangle, Info, ChevronDown, ChevronUp, Code } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Displays preview of processing rules import operations with validation results
 * @param {Object} props
 * @param {Object} props.preview - Preview data with toAdd, toUpdate arrays
 * @param {Array} props.errors - Array of error objects
 * @param {Array} props.warnings - Array of warning objects
 */
export function ProcessingRulesImportPreview({ preview, errors = [], warnings = [] }) {
  const [expandedUpdates, setExpandedUpdates] = useState({});
  const [expandedCode, setExpandedCode] = useState({});

  if (!preview && errors.length === 0 && warnings.length === 0) {
    return null;
  }

  const { toAdd = [], toUpdate = [], summary = {} } = preview || {};
  const totalRows = summary.totalRows || toAdd.length + toUpdate.length;
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;

  const toggleUpdate = (index) => {
    setExpandedUpdates(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleCode = (key) => {
    setExpandedCode(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getRuleTypeBadge = (ruleType) => {
    const colors = {
      validation: 'bg-purple-100 text-purple-800',
      transformation: 'bg-orange-100 text-orange-800'
    };
    return (
      <Badge className={colors[ruleType] || 'bg-gray-100 text-gray-800'}>
        {ruleType || 'unknown'}
      </Badge>
    );
  };

  const getLanguageBadge = (language) => {
    const colors = {
      python: 'bg-blue-100 text-blue-800',
      javascript: 'bg-yellow-100 text-yellow-800'
    };
    return (
      <Badge variant="outline" className={colors[language] || ''}>
        {language || 'unknown'}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Rows
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalRows}</div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-green-700">
              New Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{toAdd.length}</div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-blue-700">
              Updates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{toUpdate.length}</div>
          </CardContent>
        </Card>

        <Card className={hasErrors ? "border-red-200 bg-red-50" : ""}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-sm font-medium ${hasErrors ? 'text-red-700' : 'text-muted-foreground'}`}>
              Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${hasErrors ? 'text-red-700' : ''}`}>
              {errors.length}
            </div>
          </CardContent>
        </Card>

        <Card className={hasWarnings ? "border-yellow-200 bg-yellow-50" : ""}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-sm font-medium ${hasWarnings ? 'text-yellow-700' : 'text-muted-foreground'}`}>
              Warnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${hasWarnings ? 'text-yellow-700' : ''}`}>
              {warnings.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Errors Alert */}
      {hasErrors && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {errors.length} validation error{errors.length !== 1 ? 's' : ''} found. Please fix these issues before importing.
          </AlertDescription>
        </Alert>
      )}

      {/* Warnings Alert */}
      {hasWarnings && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            {warnings.length} warning{warnings.length !== 1 ? 's' : ''} found. These won't prevent import but should be reviewed.
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs for Different Sections */}
      <Tabs defaultValue={hasErrors ? "errors" : "new"} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="new" className="relative">
            New Rules
            {toAdd.length > 0 && (
              <Badge variant="secondary" className="ml-2">{toAdd.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="updates" className="relative">
            Updates
            {toUpdate.length > 0 && (
              <Badge variant="secondary" className="ml-2">{toUpdate.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="errors" className="relative">
            Errors
            {hasErrors && (
              <Badge variant="destructive" className="ml-2">{errors.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="warnings" className="relative">
            Warnings
            {hasWarnings && (
              <Badge variant="secondary" className="ml-2 bg-yellow-100 text-yellow-800">{warnings.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* New Rules Tab */}
        <TabsContent value="new" className="space-y-4">
          {toAdd.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  <Info className="h-8 w-8 mx-auto mb-2" />
                  <p>No new rules to add</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rule Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Language</TableHead>
                      <TableHead>Code</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {toAdd.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.rule_name}</TableCell>
                        <TableCell className="max-w-md truncate">{item.description || '-'}</TableCell>
                        <TableCell>{getRuleTypeBadge(item.rule_type)}</TableCell>
                        <TableCell>{getLanguageBadge(item.rule_language)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleCode(`new-${index}`)}
                          >
                            <Code className="h-4 w-4 mr-1" />
                            {expandedCode[`new-${index}`] ? 'Hide' : 'View'}
                          </Button>
                          {expandedCode[`new-${index}`] && (
                            <pre className="mt-2 p-2 bg-slate-100 rounded text-xs overflow-x-auto max-w-md">
                              {item.rule_code?.substring(0, 500)}{item.rule_code?.length > 500 ? '...' : ''}
                            </pre>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Updates Tab */}
        <TabsContent value="updates" className="space-y-4">
          {toUpdate.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  <Info className="h-8 w-8 mx-auto mb-2" />
                  <p>No existing rules to update</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {toUpdate.map((item, index) => (
                <Card key={index} className="border-blue-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-blue-600" />
                        <CardTitle className="text-base">{item.rule_name}</CardTitle>
                        {getRuleTypeBadge(item.rule_type)}
                        {getLanguageBadge(item.rule_language)}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleUpdate(index)}
                      >
                        {expandedUpdates[index] ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  {expandedUpdates[index] && (
                    <CardContent className="space-y-3">
                      {item.changes && item.changes.map((change, changeIndex) => (
                        <div key={changeIndex} className="space-y-1">
                          <div className="text-sm font-medium text-muted-foreground">
                            {change.field}:
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="p-2 bg-red-50 border border-red-200 rounded">
                              <div className="text-xs text-red-600 font-medium mb-1">Old:</div>
                              <div className="text-red-900 max-h-32 overflow-y-auto">
                                {change.field === 'rule_code' ? (
                                  <pre className="text-xs whitespace-pre-wrap">{change.oldValue?.substring(0, 300) || '(empty)'}{change.oldValue?.length > 300 ? '...' : ''}</pre>
                                ) : (
                                  change.oldValue || '(empty)'
                                )}
                              </div>
                            </div>
                            <div className="p-2 bg-green-50 border border-green-200 rounded">
                              <div className="text-xs text-green-600 font-medium mb-1">New:</div>
                              <div className="text-green-900 max-h-32 overflow-y-auto">
                                {change.field === 'rule_code' ? (
                                  <pre className="text-xs whitespace-pre-wrap">{change.newValue?.substring(0, 300) || '(empty)'}{change.newValue?.length > 300 ? '...' : ''}</pre>
                                ) : (
                                  change.newValue || '(empty)'
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {!item.changes && (
                        <div className="text-sm text-muted-foreground">
                          No changes detected (values are identical)
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Errors Tab */}
        <TabsContent value="errors" className="space-y-4">
          {errors.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-green-600">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>No validation errors found</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Column</TableHead>
                      <TableHead>Error</TableHead>
                      <TableHead>Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errors.map((error, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {error.row || '-'}
                        </TableCell>
                        <TableCell>{error.column || '-'}</TableCell>
                        <TableCell className="text-red-600">{error.message}</TableCell>
                        <TableCell className="max-w-xs truncate font-mono text-xs">
                          {error.value !== undefined ? String(error.value).substring(0, 50) : '-'}
                          {error.value && String(error.value).length > 50 ? '...' : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Warnings Tab */}
        <TabsContent value="warnings" className="space-y-4">
          {warnings.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-green-600">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>No warnings</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Column</TableHead>
                      <TableHead>Warning</TableHead>
                      <TableHead>Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {warnings.map((warning, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {warning.row || '-'}
                        </TableCell>
                        <TableCell>{warning.column || '-'}</TableCell>
                        <TableCell className="text-yellow-700">{warning.message}</TableCell>
                        <TableCell className="max-w-xs truncate font-mono text-xs">
                          {warning.value !== undefined ? String(warning.value).substring(0, 50) : '-'}
                          {warning.value && String(warning.value).length > 50 ? '...' : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
